import { useState, useEffect } from 'react'
import { X, Clock, Users, Wrench, FileText, Trash2, CheckCircle, AlertCircle, Building2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import api from '../../core/services/api'
import { ConfirmModal } from '../Toast'
import SchedePerEntita from '../schede/SchedePerEntita'
import type { GruppoEventi, EventoCalendario, TipoEvento, StatoEvento } from '../../types/calendario'
import { TIPO_EVENTO_CONFIG, STATO_EVENTO_CONFIG } from '../../types/calendario'

interface Props {
  gruppoId: string
  onClose: () => void
  onUpdated: () => void
  onDeleted: () => void
}

export default function EventoDetailModal({ gruppoId, onClose, onUpdated, onDeleted }: Props) {
  const navigate = useNavigate()
  const [gruppo, setGruppo] = useState<GruppoEventi | null>(null)
  const [loading, setLoading] = useState(true)
  const [showDelete, setShowDelete] = useState(false)
  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const res = await api.get(`/v1/calendario/eventi/gruppo/${gruppoId}`)
        setGruppo(res.data)
      } catch {
        onClose()
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [gruppoId, onClose])

  const handleChangeStato = async (eventoId: string, nuovoStato: StatoEvento) => {
    try {
      await api.put(`/v1/calendario/eventi/${eventoId}/stato?nuovo_stato=${nuovoStato}`)
      // Reload
      const res = await api.get(`/v1/calendario/eventi/gruppo/${gruppoId}`)
      setGruppo(res.data)
      onUpdated()
    } catch { /* ignore */ }
  }

  const handleDelete = async () => {
    try {
      await api.delete(`/v1/calendario/eventi/gruppo/${gruppoId}`)
      onDeleted()
    } catch { /* ignore */ }
  }

  if (loading || !gruppo) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000]">
        <div className="bg-white rounded-xl shadow-2xl p-8">
          <div className="animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full mx-auto" />
        </div>
      </div>
    )
  }

  const tipoCfg = TIPO_EVENTO_CONFIG[gruppo.tipo_evento as TipoEvento] || TIPO_EVENTO_CONFIG.lavoro
  const personale = gruppo.eventi.filter(e => e.risorsa_tipo === 'personale')
  const strumenti = gruppo.eventi.filter(e => e.risorsa_tipo === 'strumento')
  const formattedDate = new Date(gruppo.data + 'T00:00:00').toLocaleDateString('it-IT', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  })

  const statoOptions: StatoEvento[] = ['pianificato', 'confermato', 'completato']

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-[2000] pt-8 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 mb-8">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: tipoCfg.color }} />
              <span className="text-xs font-medium uppercase tracking-wider" style={{ color: tipoCfg.color }}>
                {tipoCfg.label}
              </span>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">{gruppo.titolo}</h2>
            <p className="text-sm text-gray-500 capitalize">{formattedDate}</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowDelete(true)}
              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="Elimina"
            >
              <Trash2 className="w-5 h-5" />
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Cliente (per visite commerciali) */}
          {gruppo.cliente_label && (
            <div className="flex items-center gap-3 p-3 bg-violet-50 border border-violet-100 rounded-lg">
              <Building2 className="w-5 h-5 text-violet-500" />
              <div className="flex-1">
                <p className="text-sm font-medium text-violet-900">{gruppo.cliente_label}</p>
              </div>
              <button
                onClick={() => { onClose(); navigate(`/app/fascicoli?tipo=cliente&search=${encodeURIComponent(gruppo.cliente_label || '')}`) }}
                className="text-xs text-violet-600 hover:text-violet-800 font-medium"
              >
                Vai al fascicolo
              </button>
            </div>
          )}

          {/* Commessa + Fase */}
          {gruppo.commessa_label && (
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900">{gruppo.commessa_label}</p>
                {gruppo.fase_label && (
                  <p className="text-xs text-gray-500 mt-0.5">Fase: {gruppo.fase_label}</p>
                )}
              </div>
            </div>
          )}

          {/* Costo totale */}
          {gruppo.costo_totale > 0 && (
            <div className="flex items-center justify-between px-4 py-3 bg-primary-50 border border-primary-100 rounded-lg">
              <span className="text-sm font-medium text-primary-700">Costo giornata</span>
              <span className="text-lg font-bold text-primary-700">
                {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(gruppo.costo_totale)}
              </span>
            </div>
          )}

          {/* Personale */}
          {personale.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-700">Personale</h3>
              </div>
              <div className="space-y-2">
                {personale.map(ev => (
                  <EventoRisorsaRow
                    key={ev.id}
                    evento={ev}
                    statoOptions={statoOptions}
                    onChangeStato={handleChangeStato}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Strumenti */}
          {strumenti.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Wrench className="w-4 h-4 text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-700">Strumenti / Mezzi</h3>
              </div>
              <div className="space-y-2">
                {strumenti.map(ev => (
                  <EventoRisorsaRow
                    key={ev.id}
                    evento={ev}
                    statoOptions={statoOptions}
                    onChangeStato={handleChangeStato}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Note */}
          {gruppo.eventi.some(e => e.note) && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Note</h3>
              <p className="text-sm text-gray-600">{gruppo.eventi.find(e => e.note)?.note}</p>
            </div>
          )}

          {/* Schede Dinamiche (Rapportini) */}
          <div className="border-t border-gray-200 pt-4">
            <SchedePerEntita
              entityType="evento_calendario"
              entityId={gruppoId}
              entityLabel={`${gruppo.titolo} — ${formattedDate}`}
            />
          </div>
        </div>
      </div>

      {/* Confirm delete */}
      <ConfirmModal
        isOpen={showDelete}
        title="Elimina Evento"
        message={`Eliminare l'evento "${gruppo.titolo}" del ${formattedDate} con tutte le risorse associate?`}
        confirmText="Elimina"
        onConfirm={handleDelete}
        onCancel={() => setShowDelete(false)}
        variant="danger"
      />
    </div>
  )
}

// Sub-component: row per risorsa
function EventoRisorsaRow({
  evento,
  statoOptions,
  onChangeStato,
}: {
  evento: EventoCalendario
  statoOptions: StatoEvento[]
  onChangeStato: (id: string, stato: StatoEvento) => void
}) {
  const costo = evento.costo_calcolato || 0

  const statoIcon = {
    pianificato: <AlertCircle className="w-3.5 h-3.5 text-gray-400" />,
    confermato: <Clock className="w-3.5 h-3.5 text-blue-500" />,
    completato: <CheckCircle className="w-3.5 h-3.5 text-green-500" />,
  }

  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg">
      <span className="text-sm flex-1">
        {evento.risorsa_tipo === 'personale' ? '\u{1F464}' : '\u{1F527}'} {evento.risorsa_label}
      </span>
      <span className="text-xs text-gray-500">{evento.ore}h</span>
      {costo > 0 && (
        <span className="text-xs font-medium text-gray-700">
          {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(costo)}
        </span>
      )}
      <select
        value={evento.stato}
        onChange={e => onChangeStato(evento.id, e.target.value as StatoEvento)}
        className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white"
      >
        {statoOptions.map(s => (
          <option key={s} value={s}>{STATO_EVENTO_CONFIG[s].label}</option>
        ))}
      </select>
      {statoIcon[evento.stato as keyof typeof statoIcon]}
    </div>
  )
}
